import { useEffect, useRef, useState } from 'react'
import { useLoadingStore } from '../stores/useLoadingStore'

export default function LoadingScreen() {
  const { isLoaded, isVisible, hideLoader } = useLoadingStore()
  const [displayProgress, setDisplayProgress] = useState(0)
  const [isFadingOut, setIsFadingOut] = useState(false)
  const animationRef = useRef<number | null>(null)
  const startTimeRef = useRef<number>(performance.now())
  const lastUpdateRef = useRef<number>(0)

  // Smooth continuous progress animation
  useEffect(() => {
    const totalDuration = 3500
    const startTime = startTimeRef.current

    const animate = () => {
      const now = performance.now()
      const elapsed = now - startTime
      const deltaTime = now - lastUpdateRef.current
      lastUpdateRef.current = now

      if (isLoaded) {
        // Smooth acceleration to 100 when loaded
        setDisplayProgress((prev) => {
          const remaining = 100 - prev
          // Smooth lerp with time-based increment
          const speed = 0.008 * deltaTime
          const step = remaining * speed
          return Math.min(100, prev + Math.max(step, 0.5))
        })
      } else {
        // Smooth continuous curve - no jumps
        let targetProgress: number

        if (elapsed < 500) {
          // Phase 1: Quick start (0-30%)
          targetProgress = (elapsed / 500) * 30
        } else if (elapsed < 1400) {
          // Phase 2: Steady (30-65%)
          targetProgress = 30 + ((elapsed - 500) / 900) * 35
        } else if (elapsed < 2500) {
          // Phase 3: Slower (65-88%)
          targetProgress = 65 + ((elapsed - 1400) / 1100) * 23
        } else if (elapsed < totalDuration) {
          // Phase 4: Crawl (88-95%)
          targetProgress = 88 + ((elapsed - 2500) / 1000) * 7
        } else {
          targetProgress = 95
        }

        // Smooth interpolation to target
        setDisplayProgress((prev) => {
          const diff = targetProgress - prev
          const smoothStep = diff * 0.15
          return prev + smoothStep
        })
      }

      animationRef.current = requestAnimationFrame(animate)
    }

    lastUpdateRef.current = performance.now()
    animationRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isLoaded])

  // Handle fade out when progress hits 100
  useEffect(() => {
    if (displayProgress >= 99.5 && isLoaded) {
      // Start fade immediately
      setIsFadingOut(true)
    }
  }, [displayProgress, isLoaded])

  // Hide loader after fade animation completes
  useEffect(() => {
    if (isFadingOut) {
      const fadeTimer = setTimeout(() => {
        hideLoader()
      }, 500)

      return () => clearTimeout(fadeTimer)
    }
  }, [isFadingOut, hideLoader])

  if (!isVisible) return null

  const displayNum = Math.round(displayProgress)

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        opacity: isFadingOut ? 0 : 1,
        transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: isFadingOut ? 'none' : 'auto',
      }}
    >
      {/* Logo */}
      <div
        style={{
          opacity: isFadingOut ? 0 : 1,
          transform: isFadingOut ? 'scale(1.02)' : 'scale(1)',
          transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <img
          src="/decals/Logo_horizontal.png"
          alt="Ampere Hour"
          style={{
            width: 420,
            height: 'auto',
          }}
        />
      </div>

      {/* Counter */}
      <div
        style={{
          opacity: isFadingOut ? 0 : 1,
          transform: isFadingOut ? 'translateY(8px)' : 'translateY(0)',
          transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          alignItems: 'baseline',
          fontFamily: "'Manrope', sans-serif",
          fontSize: 56,
          fontWeight: 600,
          color: 'rgba(0, 0, 0, 0.15)',
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span>{displayNum}</span>
        <span style={{ fontSize: 36, marginLeft: 4 }}>%</span>
      </div>
    </div>
  )
}
